import { CampaignsIcon, MessagesIcon } from '@make-cents/cents-design-system';

import type {
	FeatureId,
	UpsellContent,
	UpsellFeatureId,
	VisibilityState,
} from '~types/entitlements';
import { NavItemId } from '~types/nav';

/**
 * Entitlement vocabulary for `GET /business-owner/entitlements` (COS-4843). The backend owns the
 * contract — CentsBE `src/constants/entitlements/featureCatalog.ts` and `vocabulary.ts` — so these
 * mirror it rather than define it.
 */
export const ENTITLEMENTS_QUERY_KEY = 'business-owner-entitlements';

/**
 * Change notifications (COS-4956). CentsBE `src/constants/pusher.ts` owns both strings; a client binds
 * to the exact event name, so they must match it verbatim.
 */
export const BUSINESS_CHANNEL_PREFIX = 'private-business-';
export const ENTITLEMENTS_CHANGED_EVENT = 'entitlements-changed';

export const FEATURE_IDS = {
	DASHBOARDS: 'dashboards',
	CUSTOMERS: 'customers',
	CUSTOMERS_INVOICES: 'customers.invoices',
	CUSTOMERS_LAUNDRY_CARDS: 'customers.laundryCards',
	MACHINES: 'machines',
	MACHINES_VACS: 'machines.vacs',
	MACHINES_EQUIPMENT_ISSUES: 'machines.equipmentIssues',
	REPORTS: 'reports',
	REPORTS_LAUNDROWORKS: 'reports.laundroworks',
	INTEGRATIONS: 'integrations',
	LOCATIONS: 'locations',
	TEAM: 'team',
	ACCOUNT: 'account',
	MARKETING: 'marketing',
	CONTACT_CENTER: 'contactCenter',
	BARRY_AI: 'barryAI',
	ORDERS: 'orders',
	DISPATCH: 'dispatch',
	PRODUCTS_AND_SERVICES: 'productsAndServices',
	TASKS: 'tasks',
	PROMOTIONS: 'promotions',
} as const;

export const VISIBILITY_STATES = {
	SHOW: 'SHOW',
	SUB: 'SUB',
	UPSELL: 'UPSELL',
} as const;

export const ENTITLEMENT_SOURCES = {
	ENTITLEMENTS: 'entitlements',
	FALLBACK: 'fallback',
} as const;

/** Precedence when several selected stores disagree: SHOW > SUB > UPSELL. */
export const VISIBILITY_STRENGTH: Record<VisibilityState, number> = {
	[VISIBILITY_STATES.SHOW]: 3,
	[VISIBILITY_STATES.SUB]: 2,
	[VISIBILITY_STATES.UPSELL]: 1,
};

/** Dashboard items entitlements can hide. Marketing is absent: it is resolved inline, since it can upsell. */
export const DASHBOARD_NAV_ITEM_FEATURES: Partial<Record<NavItemId, FeatureId>> = {
	[NavItemId.Dashboards]: FEATURE_IDS.DASHBOARDS,
	[NavItemId.Orders]: FEATURE_IDS.ORDERS,
	[NavItemId.Customers]: FEATURE_IDS.CUSTOMERS,
	[NavItemId.EQUIPMENT]: FEATURE_IDS.MACHINES,
	[NavItemId.Dispatch]: FEATURE_IDS.DISPATCH,
	[NavItemId.Reports]: FEATURE_IDS.REPORTS,
	[NavItemId.INTEGRATIONS]: FEATURE_IDS.INTEGRATIONS,
};

/** Admin items entitlements can hide. */
export const ADMIN_NAV_ITEM_FEATURES: Partial<Record<NavItemId, FeatureId>> = {
	[NavItemId.Locations]: FEATURE_IDS.LOCATIONS,
	[NavItemId.Team]: FEATURE_IDS.TEAM,
	[NavItemId.ProductsAndServices]: FEATURE_IDS.PRODUCTS_AND_SERVICES,
	[NavItemId.Tasks]: FEATURE_IDS.TASKS,
	[NavItemId.Promotions]: FEATURE_IDS.PROMOTIONS,
	[NavItemId.Account]: FEATURE_IDS.ACCOUNT,
};

// Copy from the "Connect LW BM" prototype (Claude Design, Modular Business Manager).
export const UPSELL_CONTENT: Record<UpsellFeatureId, UpsellContent> = {
	[FEATURE_IDS.MARKETING]: {
		pageTitle: 'Marketing',
		Icon: CampaignsIcon,
		eyebrow: 'Available with Cents Accelerate',
		title: 'Unlock Marketing with Cents Accelerate',
		body: 'Send blasts, build automated journeys, design email templates, request reviews, run ads, and edit your storefront. All in one place.',
		cta: 'Add Cents Accelerate',
	},
	[FEATURE_IDS.CONTACT_CENTER]: {
		pageTitle: 'Contact Center',
		Icon: MessagesIcon,
		eyebrow: 'Available with Assist or Accelerate',
		title: 'Unlock Contact Center with Assist or Accelerate',
		body: 'Reach customers with two-way messaging, push notifications, and laundry status updates. Available on AI Cents Assist or Cents Accelerate.',
		cta: 'Add subscription',
	},
};
